import * as React from 'react';
import {trackConversion} from '../components/PageMetadata';
import {Suspense, useState} from 'react';
import {graphql, useLazyLoadQuery} from 'react-relay';
import {Link, useSearchParams} from 'react-router-dom';
import {Check} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import EngagementBuilder from '../engagement/EngagementBuilder';
import {INTERESTS} from '../engagement/draft';
import {StartProjectPageQuery} from './__generated__/StartProjectPageQuery.graphql';

const QUERY = graphql`
    query StartProjectPageQuery {
        viewer { id name email customer { name email phone company } }
    }`;

// Public host for the engagement builder: anyone can start one; the contact step
// is prefilled when signed in. /start?interest=printing preselects a tile.
const PublicBuilder: React.FC = () => {
    const data = useLazyLoadQuery<StartProjectPageQuery>(QUERY, {}, {fetchPolicy: 'store-and-network'});
    const viewer = data.viewer;
    const [params] = useSearchParams();
    const preselected = (params.get('interest') ?? '').split(',').filter((k) => INTERESTS.some((i) => i.key === k));
    const [done, setDone] = useState<{ name: string; email: string } | null>(null);

    if (done) {
        return (
            <div className="max-w-xl mx-auto text-center border border-gray-800 bg-[#0a0a0f] p-10 rounded-2xl">
                <Check className="w-10 h-10 text-emerald-400 mx-auto mb-4"/>
                <h2 className="text-2xl font-display font-bold mb-2">Got it{done.name ? `, ${done.name.split(' ')[0]}` : ''}.</h2>
                <p className="text-gray-400 text-sm mb-6">We'll read it properly and come back to you{done.email && <> at <span className="text-white">{done.email}</span></>}, usually within a business day.</p>
                <div className="flex justify-center gap-3">
                    <Link to="/book" className="px-5 py-3 border border-gray-700 text-gray-300 text-xs font-bold tracking-widest uppercase hover:border-lumina-accent hover:text-white">Browse gear</Link>
                    {viewer
                        ? <Link to="/client" className="px-5 py-3 bg-lumina-accent text-black text-xs font-bold tracking-widest uppercase hover:bg-white">Your portal</Link>
                        : <Link to="/" className="px-5 py-3 bg-lumina-accent text-black text-xs font-bold tracking-widest uppercase hover:bg-white">Home</Link>}
                </div>
            </div>
        );
    }

    const contact = {
        name: viewer?.customer?.name ?? viewer?.name ?? '', email: viewer?.customer?.email ?? viewer?.email ?? '',
        company: viewer?.customer?.company ?? '', phone: viewer?.customer?.phone ?? '',
    };

    return (
        <EngagementBuilder
            host="public"
            initialDraft={{...(viewer ? contact : {}), interests: preselected}}
            signedInAs={viewer?.name ?? null}
            onSubmitted={(_id, draft) => {
                trackConversion('generate_lead', 'project_inquiry');
                setDone({name: draft.name || contact.name, email: draft.email || contact.email});
            }}
        />
    );
};

const StartProjectPage: React.FC = () => (
    <div className="bg-lumina-dark min-h-screen text-white">
        <Navbar/>
        <main className="max-w-7xl mx-auto px-6 pt-28 pb-24">
            <div className="text-center mb-10">
                <span className="text-lumina-purple font-display text-sm tracking-wider uppercase">Start a project</span>
                <h1 className="text-4xl md:text-5xl font-display font-bold mt-2">LET'S <span className="text-gray-600">MAKE IT</span></h1>
            </div>
            <Suspense fallback={<p className="text-gray-500 text-sm text-center">Loading…</p>}>
                <PublicBuilder/>
            </Suspense>
        </main>
        <Footer/>
    </div>
);

export default StartProjectPage;
